import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Bouton, Carte, CaseACocher, Chiffre, Chiffres, EnTetePage, Etiquette,
  IconeAbonnement, IconeAlerte, Message, Squelette, useRetours,
} from '@journal/ui';

/**
 * Version du texte de renonciation affiché ci-dessous.
 *
 * Doit correspondre à `VERSION_RENONCIATION` côté serveur, qui refuse toute
 * autre valeur. Modifier le texte sans changer les deux constantes ferait
 * enregistrer un consentement à un texte qu'on ne saurait plus reconstituer —
 * c'est-à-dire un consentement qui ne prouve rien.
 */
const VERSION_RENONCIATION = 'retractation-2026-10-14';
import { messageErreur, useSession } from '../lib/session.tsx';
import type {
  Abonnement as DonneesAbonnement, UtilisateurCourant,
} from '../lib/api.ts';
import { ChoixOffre, type OffrePublique } from '../components/ChoixOffre.tsx';
import { FacturesEtAvoirs } from '../components/FacturesEtAvoirs.tsx';

/** Centimes → « 7,33 € ». */
const euros = (centimes: number) =>
  (centimes / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

/** États d'abonnement, en français et sans jargon Stripe. */
const ETATS: Record<string, { texte: string; ton: 'info' | 'succes' | 'attention' | 'erreur' }> = {
  ESSAI: { texte: "Période d'essai", ton: 'info' },
  ESSAI_EXPIRE: { texte: 'Essai terminé', ton: 'attention' },
  TRIALING: { texte: "Période d'essai", ton: 'info' },
  ACTIVE: { texte: 'Actif', ton: 'succes' },
  PAST_DUE: { texte: 'Paiement en attente', ton: 'attention' },
  UNPAID: { texte: 'Suspendu', ton: 'erreur' },
  CANCELED: { texte: 'Résilié', ton: 'erreur' },
  AUCUN: { texte: 'Aucun abonnement', ton: 'info' },
};

/**
 * Noms d'offres présentables.
 *
 * L'API renvoie le code technique — « ENSEIGNANT », « ECOLE ». L'afficher tel
 * quel donne à cet écran l'allure d'une console d'administration, et force le
 * lecteur à deviner ce qu'il paie. Le repli sur le code brut est conservé :
 * une offre ajoutée côté serveur ne doit pas produire une case vide.
 *
 * « SOLO » figurait ici et ne correspondait à aucune offre : un vestige d'un
 * catalogue antérieur, qui n'aurait jamais pu s'afficher.
 */
const NOMS_OFFRES: Record<string, string> = {
  ENSEIGNANT: 'Enseignant',
  ECOLE: 'École',
  ESSAI: 'Essai',
  ESSAI_EXPIRE: 'Essai terminé',
};

/*
 * Les ÉLÈVES n'y figurent plus : le serveur ne les rend pas. Une école
 * n'achète pas des élèves, elle achète des comptes d'enseignants, et le
 * décompte donnait l'impression contraire.
 */
const LIBELLE_QUOTA: Record<string, string> = {
  users: 'Utilisateurs',
  school_years: 'Années scolaires',
};

export function Abonnement() {
  const { api } = useSession();
  const { confirmer, notifier } = useRetours();
  const [donnees, setDonnees] = useState<DonneesAbonnement | null>(null);
  const [retractationEnCours, setRetractationEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [versPortail, setVersPortail] = useState(false);
  /*
   * Décochée par défaut, et sans mémoire d'une visite précédente : une case
   * pré-cochée ne vaut pas consentement, et un consentement donné il y a trois
   * mois ne vaut pas pour l'achat d'aujourd'hui.
   */
  const [renonce, setRenonce] = useState(false);
  const [offres, setOffres] = useState<OffrePublique[]>([]);
  const [moi, setMoi] = useState<UtilisateurCourant | null>(null);

  useEffect(() => {
    api
      .requete<DonneesAbonnement>('/billing/subscription')
      .then(setDonnees)
      .catch((e: unknown) => setErreur(messageErreur(e)));
  }, [api]);

  /*
   * Catalogue et rôle, chargés à part.
   *
   * Leur échec ne doit PAS masquer l'état de l'abonnement : celui-ci explique
   * pourquoi l'application refuse d'enregistrer, et c'est l'information la plus
   * urgente de l'écran. Le choix d'offre disparaît simplement.
   */
  useEffect(() => {
    api.requete<OffrePublique[]>('/billing/plans').then(setOffres).catch(() => setOffres([]));
    api.requete<UtilisateurCourant>('/auth/me').then(setMoi).catch(() => setMoi(null));
  }, [api]);

  if (erreur && !donnees) return <Message ton="erreur">{erreur}</Message>;

  if (!donnees) {
    return (
      <>
        <EnTetePage titre="Abonnement" />
        <div className="flex flex-col gap-4">
          <Squelette className="h-40" />
          <Squelette className="h-52" />
        </div>
      </>
    );
  }

  const etat = ETATS[donnees.status] ?? { texte: donnees.status, ton: 'info' as const };
  const offre = NOMS_OFFRES[donnees.planCode] ?? donnees.planCode;

  /*
   * Un abonnement à résilier existe-t-il ?
   *
   * Les mêmes statuts que ceux qui interdisent d'en souscrire un second côté
   * serveur : proposer « Résilier » à un espace en période d'essai n'aurait
   * rien à annuler, et l'inquiéterait pour rien.
   */
  const aUnAbonnement = ['TRIALING', 'ACTIVE', 'PAST_DUE'].includes(donnees.status);
  /*
   * Le portail Stripe change une carte, montre les factures, résilie — mais
   * ne REPREND pas un abonnement terminé. Le proposer à un espace résilié
   * l'envoyait dans une impasse : on se reprend par « Souscrire », ici.
   */
  const peutGererLePaiement = aUnAbonnement || donnees.status === 'UNPAID';
  const termine = donnees.status === 'CANCELED';
  /*
   * Un abonnement terminé se REPREND, il ne se rechoisit pas : on ne propose
   * que l'offre que l'espace avait. Changer d'offre reste possible ensuite,
   * depuis le portail. Faute de la retrouver au catalogue, tout est proposé.
   */
  const offresProposees = termine && offres.some((o) => o.code === donnees.planCode)
    ? offres.filter((o) => o.code === donnees.planCode)
    : offres;
  /** Pourquoi la saisie est bloquée, et comment la retrouver : selon le cas. */
  const blocage = donnees.status === 'UNPAID' || donnees.status === 'PAST_DUE'
    ? 'Le dernier paiement n’a pas abouti. Mettez à jour votre moyen de paiement (« Gérer le paiement ») pour retrouver la saisie.'
    : termine
      ? 'Votre abonnement a pris fin. Reprenez-le ci-dessous pour retrouver la saisie.'
      : 'Votre période d’essai est terminée. Choisissez une offre ci-dessous pour retrouver la saisie.';
  const dateFin = donnees.currentPeriodEnd
    ? new Date(donnees.currentPeriodEnd).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    })
    : null;

  async function retracter() {
    const r = donnees?.retractation;
    if (!r) return;
    const ok = await confirmer({
      titre: 'Vous rétracter de votre abonnement ?',
      message: `L’abonnement prend fin immédiatement et ${euros(r.remboursement)} vous `
        + 'sont remboursés. Vos données restent exportables pendant '
        + 'quatre-vingt-dix jours.',
      libelleConfirmation: 'Me rétracter',
      destructeur: true,
    });
    if (!ok) return;
    setRetractationEnCours(true);
    try {
      const fait = await api.requete<{ rembourse: number }>('/billing/retractation', { methode: 'POST' });
      notifier(`Rétractation enregistrée : ${euros(fait.rembourse)} vous sont remboursés.`);
      setDonnees(await api.requete<DonneesAbonnement>('/billing/subscription'));
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setRetractationEnCours(false);
    }
  }

  async function souscrire(
    offreChoisie: OffrePublique, periodicite: 'MONTH' | 'YEAR', sieges?: number,
  ) {
    try {
      const r = await api.requete<{ url: string }>('/billing/checkout-session', {
        methode: 'POST',
        corps: {
          planCode: offreChoisie.code,
          interval: periodicite,
          // Étalement conditionnel : `exactOptionalPropertyTypes` distingue
          // « absent » de « présent et indéfini », et le serveur refuserait un
          // `seats: null` explicite.
          ...(sieges === undefined ? {} : { seats: sieges }),
          // La VERSION du texte affiché, et non un simple « oui » : le serveur
          // refuse celle qu'il ne connaît pas, et l'audit la conserve.
          renonciationRetractation: VERSION_RENONCIATION,
        },
      });
      window.location.href = r.url;
    } catch (e: unknown) {
      setErreur(messageErreur(e));
    }
  }

  function ouvrirPortail() {
    setVersPortail(true);
    void api
      .requete<{ url: string }>('/billing/portal-session', { methode: 'POST' })
      .then((r) => {
        window.location.href = r.url;
      })
      .catch((e: unknown) => {
        setErreur(messageErreur(e));
        setVersPortail(false);
      });
  }

  return (
    <>
      <EnTetePage
        titre="Abonnement"
        contexte={`Offre ${offre} · ${etat.texte.toLowerCase()}`}
        actions={
          <>
            {peutGererLePaiement && (
              <Bouton
                variante="primary"
                icone={<IconeAbonnement taille={16} />}
                chargement={versPortail}
                onClick={ouvrirPortail}
              >
                Gérer le paiement
              </Bouton>
            )}
            {/*
              * « Résilier », nommé, et à côté du reste.
              *
              * L'article L215-1-1 du code de la consommation impose depuis juin
              * 2023 que résilier en ligne soit aussi simple que souscrire. Le
              * portail Stripe fait le travail — mais caché derrière « Gérer le
              * paiement », le chemin n'est ni nommé ni évident. Un bouton qui
              * dit ce qu'il fait n'est pas un détail de formulation : c'est la
              * condition qui distingue un parcours conforme d'un parcours qui
              * ne l'est pas.
              *
              * Il mène au même portail : c'est là que Stripe présente la
              * résiliation, avec ses obligations propres à chaque pays.
              */}
            {aUnAbonnement && (
              <Bouton
                variante="discret"
                chargement={versPortail}
                onClick={ouvrirPortail}
              >
                Résilier
              </Bouton>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {erreur && <Message ton="erreur">{erreur}</Message>}

        {/*
         * L'avertissement d'écriture suspendue passe AVANT tout le reste.
         * Placé en bas de la première carte comme auparavant, il se lisait
         * après les chiffres — or c'est la seule information de l'écran qui
         * explique pourquoi l'application refuse d'enregistrer.
         */}
        {/*
         * Quand l'espace n'est pas payé, le cadre de l'application affiche
         * déjà, au-dessus, POURQUOI il est fermé : le redire ici doublait le
         * message. Il ne reste utile que pour un espace ouvert dont la saisie
         * est bloquée (paiement en retard).
         */}
        {!donnees.writesAllowed && donnees.paid !== false && (
          <Message ton="attention" titre="Les modifications sont suspendues">
            {`Vos données restent exportables. ${blocage}`}
          </Message>
        )}

        {/*
         * Le choix d'offre n'apparaît QUE si l'abonnement n'est pas déjà en
         * cours. Changer d'offre passe par le portail Stripe, qui sait gérer
         * les prorata ; proposer ici un second achat créerait un doublon de
         * facturation.
         *
         * Placé EN TÊTE, juste sous le message : c'est la seule chose à faire
         * sur cet écran quand l'abonnement ne court pas. Plus bas, après la
         * consommation, on ne le voyait pas sans faire défiler.
         */}
        {['ESSAI', 'ESSAI_EXPIRE', 'AUCUN', 'CANCELED'].includes(donnees.status) && (
          <>
            <ChoixOffre
              offres={offresProposees}
              {...(termine ? { titre: 'Reprendre votre abonnement' } : {})}
              comptesOuverts={
                donnees.quotas.find((q) => q.metric === 'users')?.used ?? 1
              }
              estProprietaire={moi?.role === 'OWNER'}
              surSouscription={souscrire}
              renonciationAcceptee={renonce}
              sousLesOffres={(
                <>
                  {/*
                    * Une seule case, deux choses dites : l'acceptation des CGV,
                    * et la demande EXPRESSE de commencer pendant le délai de
                    * rétractation (L221-25). Sans la seconde, l'abonnement ne
                    * pourrait commencer qu'au bout des quatorze jours. Le détail
                    * (rétractation, résiliation, export des données) est dans
                    * les CGV, ouvertes dans un nouvel onglet pour ne pas perdre
                    * le choix en cours.
                    */}
                  <CaseACocher
                    checked={renonce}
                    onChange={(e) => setRenonce(e.target.checked)}
                    libelle={(
                      <strong>
                        J’accepte les{' '}
                        <Link to="/cgv" target="_blank" rel="noopener" className="underline">
                          conditions générales de vente
                        </Link>{' '}
                        et je demande que mon abonnement commence dès maintenant, pendant
                        mon délai de rétractation de 14 jours.
                      </strong>
                    )}
                  />
                </>
              )}
            />

            {/*
             * ── Exécution immédiate, demandée expressément ───────────────────
             *
             * Un abonnement ne peut commencer avant la fin des quatorze jours de
             * rétractation que si le client le DEMANDE expressément (article
             * L221-25 du code de la consommation). Il ne perd pas son droit
             * pour autant : s'il se rétracte, il ne doit que les jours écoulés
             * — c'est ce que dit la case, et ce que fait « Me rétracter ».
             * L'ancien texte parlait de « renoncer », ce qui était faux pour un
             * abonnement (le droit ne s'éteint qu'à service entièrement fourni).
             *
             * Elle est DÉCOCHÉE par défaut, et le bouton de souscription reste
             * inactif tant qu'elle ne l'est pas : une case pré-cochée ne vaut
             * pas consentement, et la présenter après le paiement ne vaudrait
             * rien non plus. Elle vit SOUS les boutons « Souscrire », dans la
             * carte des offres (`sousLesOffres`) : séparée dans une carte plus
             * bas, on ne comprenait pas pourquoi les boutons restaient grisés.
             */}
          </>
        )}


        <Carte titre="Votre offre" action={<Etiquette ton={etat.ton}>{etat.texte}</Etiquette>}>
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
                Formule
              </dt>
              <dd className="mt-1 text-[15px] font-semibold">{offre}</dd>
            </div>
            {dateFin && !termine && (
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
                  {donnees.cancelAtPeriodEnd ? 'Prend fin le' : 'Prochain renouvellement'}
                </dt>
                <dd className="mt-1 text-[15px] font-semibold">{dateFin}</dd>
              </div>
            )}
          </dl>

          {donnees.cancelAtPeriodEnd && dateFin && (
            <div className="mt-4">
              <Message ton="attention">
                La résiliation est enregistrée. L'accès reste complet jusqu'au
                {` ${dateFin}`}.
              </Message>
            </div>
          )}
        </Carte>

        {/*
          * ── Droit de rétractation ───────────────────────────────────────────
          * Exerçable EN LIGNE, aussi simplement que la souscription, pendant
          * les quatorze jours qui suivent : sans ce bouton, le droit existait
          * sur le papier des CGV, et le client devait deviner à qui écrire.
          * Le montant rendu est affiché AVANT de confirmer.
          */}
        {donnees.retractation && moi?.role === 'OWNER' && (
          <Carte titre="Droit de rétractation">
            <p className="text-[14px] leading-relaxed">
              Vous pouvez vous rétracter jusqu’au{' '}
              <strong>{new Date(donnees.retractation.jusquAu).toLocaleDateString('fr-FR', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}</strong>. L’abonnement prend fin aussitôt ; seuls les{' '}
              {donnees.retractation.joursUtilises} jour
              {donnees.retractation.joursUtilises > 1 ? 's' : ''} déjà écoulé
              {donnees.retractation.joursUtilises > 1 ? 's' : ''} restent dus, et{' '}
              <strong>{euros(donnees.retractation.remboursement)}</strong> vous
              seraient remboursés aujourd’hui sur le moyen de paiement utilisé.
            </p>
            <div className="mt-4">
              <Bouton variante="secondary" chargement={retractationEnCours} onClick={retracter}>
                Me rétracter
              </Bouton>
            </div>
          </Carte>
        )}

        <Carte
          titre="Consommation"
          description="Ce que votre offre autorise, et ce qui est utilisé aujourd'hui."
        >
          <Chiffres>
            {donnees.quotas.map((q) => {
              const proportion = q.unlimited ? 0 : Math.min(q.used / Math.max(q.limit, 1), 1);
              const proche = !q.unlimited && proportion >= 0.9;
              const atteint = !q.unlimited && q.used >= q.limit;
              return (
                // `ton` et `icone` sont répartis par étalement plutôt que
                // passés à `undefined` : `exactOptionalPropertyTypes` distingue
                // « propriété absente » de « présente et indéfinie ».
                <Chiffre
                  key={q.metric}
                  libelle={LIBELLE_QUOTA[q.metric] ?? q.metric}
                  valeur={q.used}
                  {...(atteint ? { ton: 'erreur' as const }
                    : proche ? { ton: 'attention' as const } : {})}
                  {...(proche ? { icone: <IconeAlerte taille={16} /> } : {})}
                  detail={
                    <>
                      {q.unlimited ? 'Sans limite' : `sur ${q.limit} inclus`}
                      {!q.unlimited && (
                        // Barre purement décorative : la valeur chiffrée
                        // au-dessus porte l'information, lisible sans
                        // percevoir la couleur.
                        <span
                          aria-hidden="true"
                          className="mt-2 block h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-hover)]"
                        >
                          <span
                            className={[
                              'block h-full rounded-full transition-[width] duration-300',
                              'motion-reduce:transition-none',
                              atteint ? 'bg-[var(--color-danger)]'
                                : proche ? 'bg-[var(--color-warning)]'
                                  : 'bg-[var(--color-primary)]',
                            ].join(' ')}
                            style={{ width: `${proportion * 100}%` }}
                          />
                        </span>
                      )}
                    </>
                  }
                />
              );
            })}
          </Chiffres>
        </Carte>

        <Carte
          titre={peutGererLePaiement ? 'Paiement et factures' : 'Factures'}
          {...(peutGererLePaiement
            ? { description: 'Moyens de paiement, changement d’offre et résiliation sont gérés par notre prestataire de paiement.' }
            : {})}
        >
          {peutGererLePaiement && (
            <Bouton
              variante="secondary"
              icone={<IconeAbonnement taille={16} />}
              chargement={versPortail}
              onClick={ouvrirPortail}
            >
              Ouvrir l’espace de facturation
            </Bouton>
          )}
          {/*
            * ── Dit ici, où la question se pose ─────────────────────────────
            * Le portail règle le moyen de paiement ; il ne règle pas « j'ai
            * payé deux fois » ni « mon prélèvement est passé mais rien ne
            * s'est ouvert ». Pour ceux-là il faut quelqu'un — et la catégorie
            * facturation reste ouverte même quand l'abonnement ne court plus,
            * précisément parce que c'est alors qu'on en a besoin.
            */}
          <p className="mt-3 text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
            {'Un problème de paiement ou d’accès ? '}
            <Link to="/support" className="underline">Écrivez-nous</Link>
            {' : cette porte reste ouverte même si l’abonnement est suspendu.'}
          </p>
        </Carte>

        <FacturesEtAvoirs />
      </div>
    </>
  );
}
